import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { ProductsState, PRODUCTS_SLICE_NAME, initialState } from './models';
import { CANNOT_PUBLISH_WITHOUT_PRICE_CODE } from '../helpers/constants';
import {
  getProduct,
  deleteProduct,
  getProductsList,
  getStoresList,
  getProductsOfMonthList,
  getTopProductList,
  updateProductsOfMonth,
  updateTopProduct,
  createProductsOfMonth,
  createTopProduct,
  toggleActiveBulkProduct,
  deleteBulkProduct,
  updateProduct,
  updateProductFilters,
  getProductVariations,
  deleteProductVariation,
  addProductVariation,
  updateProductSeo,
  getProductRegionsAndPrices,
  updateRegionsAndPricesItem,
  bulkUpdateRegionsAndPrices,
  deleteProductsOfMonth,
  deleteTopProducts,
  reorderTopProduct,
  createProduct,
} from './actionCreators';
import { TGetProductsOfMonthQuery, TGetProductsQuery } from '../helpers';
import { IGetProductResponse, IProduct } from '@/models/products.model';

export const slice = createSlice({
  name: PRODUCTS_SLICE_NAME,
  initialState,
  reducers: {
    resetProductsListState() {
      return { ...initialState };
    },
    resetProductsList(state) {
      state.productsList = initialState.productsList;
    },
    changeQuery: (state: ProductsState, action: PayloadAction<TGetProductsQuery>) => {
      state.query = action.payload;
    },
    changeProductOfMonthQuery: (state: ProductsState, action: PayloadAction<TGetProductsOfMonthQuery>) => {
      state.productsOfMonthQuery = action.payload;
    },
    changeTopProductPosition: (state: ProductsState, action) => {
      state.topProductsList = action.payload;
    },
    resetProductOfMonth(state: ProductsState) {
      state.productsOfMonth = initialState.productsOfMonth;
    },
    resetProduct(state: ProductsState) {
      state.product = initialState.product;
    },
    clearPublishValidationError(state: ProductsState) {
      state.isPublishValidationError = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductsList.fulfilled, (state: ProductsState, action) => {
        state.productsList = action.payload;
        state.isLoading = false;
      })
      .addCase(getStoresList.fulfilled, (state: ProductsState, action) => {
        state.storeList = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getProductsOfMonthList.fulfilled, (state: ProductsState, action) => {
        state.productsOfMonthList = action.payload;
        state.isLoading = false;
      })
      .addCase(getTopProductList.fulfilled, (state: ProductsState, action) => {
        state.topProductsList = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getProductVariations.fulfilled, (state: ProductsState, action) => {
        state.productVariations = action.payload.data;
        state.isLoading = false;
      })
      .addCase(deleteProductVariation.fulfilled, (state: ProductsState, action) => {
        if (!state.productVariations) return;
        state.productVariations = state.productVariations.filter((el) => el.id !== action.payload);
        state.isLoading = false;
      })
      .addCase(addProductVariation.fulfilled, (state: ProductsState, action) => {
        if (!state.productVariations) return;
        state.productVariations = [...state.productVariations, action.payload];
        state.isLoading = false;
      })
      .addCase(getProduct.fulfilled, (state: ProductsState, action) => {
        state.product = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getProductRegionsAndPrices.fulfilled, (state: ProductsState, action) => {
        state.productRegionsAndPrices = action.payload.data;
        state.isLoading = false;
      })
      .addCase(toggleActiveBulkProduct.fulfilled, (state: ProductsState, action) => {
        if (state.productsList) {
          state.productsList = {
            ...state.productsList,
            data: state.productsList?.data?.map((product) => {
              return {
                ...product,
                active: action.meta.arg.ids?.includes(product.id) ? !product.active : product.active,
              };
            }) as IProduct[],
          };

          state.isLoading = false;
        }

        if (state.product && action.meta.arg.ids?.includes(state.product.id)) {
          state.product = { ...state.product, active: !state.product.active };
          state.isLoading = false;
        }
      })
      .addCase(updateProduct.fulfilled, (state: ProductsState, action: PayloadAction<IGetProductResponse>) => {
        const updatedProduct = action.payload.data;
        if (state.product?.id === updatedProduct.id) {
          state.product = updatedProduct;
        }

        if (state.productsList) {
          state.productsList = {
            ...state.productsList,
            data: state.productsList.data.map((product) =>
              product.id === updatedProduct.id ? updatedProduct : product,
            ),
          };
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateProductSeo.fulfilled, (state: ProductsState, action: PayloadAction<IGetProductResponse>) => {
        const updatedProduct = action.payload.data;
        if (state.product?.id === updatedProduct.id) {
          state.product = updatedProduct;
        }

        if (state.productsList) {
          state.productsList = {
            ...state.productsList,
            data: state.productsList.data.map((product) =>
              product.id === updatedProduct.id ? updatedProduct : product,
            ),
          };
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateProductFilters.fulfilled, (state: ProductsState, action: PayloadAction<IGetProductResponse>) => {
        const updatedProduct = action.payload.data;
        if (state.product?.id === updatedProduct.id) {
          state.product = updatedProduct;
        }

        if (state.productsList) {
          state.productsList = {
            ...state.productsList,
            data: state.productsList.data.map((product) =>
              product.id === updatedProduct.id ? updatedProduct : product,
            ),
          };
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateRegionsAndPricesItem.fulfilled, (state: ProductsState, action) => {
        const updatedRegionsAndPricesItem = action.payload.data;

        if (state.productRegionsAndPrices) {
          state.productRegionsAndPrices = state.productRegionsAndPrices.map((item) =>
            item.id === updatedRegionsAndPricesItem.id ? updatedRegionsAndPricesItem : item,
          );
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(bulkUpdateRegionsAndPrices.fulfilled, (state: ProductsState) => {
        state.isLoading = false;
        state.error = null;
      })
      .addMatcher(
        isFulfilled(
          createProductsOfMonth,
          createTopProduct,
          updateProductsOfMonth,
          updateTopProduct,
          reorderTopProduct,
        ),
        (state: ProductsState) => {
          state.isLoading = false;
        },
      )
      .addMatcher(isFulfilled(deleteProduct), (state: ProductsState) => {
        state.isLoading = false;
        state.error = null;
      })
      .addMatcher(
        isPending(
          getProductsList,
          getStoresList,
          getProductsOfMonthList,
          getTopProductList,
          updateProductsOfMonth,
          updateTopProduct,
          createProductsOfMonth,
          createTopProduct,
          deleteProductsOfMonth,
          deleteTopProducts,
          deleteProduct,
          deleteBulkProduct,
          toggleActiveBulkProduct,
          getProductVariations,
          addProductVariation,
          getProductRegionsAndPrices,
          updateRegionsAndPricesItem,
          bulkUpdateRegionsAndPrices,
          updateProduct,
          deleteProductVariation,
          createProduct,
          reorderTopProduct,
          getProduct,
        ),
        (state: ProductsState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(isRejected(deleteBulkProduct, deleteProduct), (state: ProductsState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;
      })
      .addMatcher(isRejected(toggleActiveBulkProduct), (state: ProductsState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;

        if (error.code === CANNOT_PUBLISH_WITHOUT_PRICE_CODE) {
          state.isPublishValidationError = true;
        } else {
          showApiErrors(error);
        }
      })
      .addMatcher(
        isRejected(
          getProductsList,
          getStoresList,
          getProductsOfMonthList,
          getTopProductList,
          updateProductsOfMonth,
          updateTopProduct,
          createProductsOfMonth,
          createTopProduct,
          deleteProductsOfMonth,
          deleteTopProducts,
          getProduct,
          updateProduct,
          updateProductFilters,
          getProductVariations,
          deleteProductVariation,
          addProductVariation,
          updateProductSeo,
          getProductRegionsAndPrices,
          updateRegionsAndPricesItem,
          bulkUpdateRegionsAndPrices,
          createProduct,
          reorderTopProduct,
        ),
        (state: ProductsState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export const {
  resetProductsListState,
  changeQuery,
  resetProduct,
  resetProductsList,
  changeProductOfMonthQuery,
  resetProductOfMonth,
  changeTopProductPosition,
  clearPublishValidationError,
} = slice.actions;

export default slice.reducer;
